<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from "vue";
  import { useRoute } from "vue-router";
  import { storeToRefs } from "pinia";
  import { LoadingState } from "@/components/episto-ui/loading-state";
  import { useReportThemesStore } from "@stores/reportThemes/reportThemes";
  import ThreePanePage from "@/templates/ThreePanePage.vue";
  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { Accordion } from "@/components/ui/accordion";
  import ReportThemeGeneralSection from "./ReportThemeGeneralSection.vue";
  import ReportThemeSkinSection from "./ReportThemeSkinSection.vue";
  import ReportThemeChartsSection from "./ReportThemeChartsSection.vue";
  import type { TextStyle, SkinKey } from "@/types";

  const skinSections: { key: SkinKey; label: string; hasTexts: boolean }[] = [
    { key: "cover", label: "Cover slide", hasTexts: true },
    { key: "section", label: "Section slides", hasTexts: true },
    { key: "content", label: "Content slides", hasTexts: true },
    { key: "end", label: "End slide", hasTexts: false },
  ];

  const route = useRoute();
  const themeToken = computed(() => route.params.themeToken as string);

  const store = useReportThemesStore();
  const { currentTheme, loading } = storeToRefs(store);

  const openSection = ref("general");

  // Shared by the skin and charts sections; derived once here from the theme.
  const detectedFonts = computed<string[]>(() => {
    const fromBg = ((currentTheme.value?.backgrounds as Record<string, unknown>)?.detected_fonts ?? []) as string[];
    const fromStyles = Object.values(currentTheme.value?.text_styles ?? {})
      .map((s) => (s as TextStyle)?.font)
      .filter(Boolean) as string[];
    return [...new Set([...fromBg, ...fromStyles])];
  });

  onMounted(async () => {
    await store.fetchTheme(themeToken.value);
    store.initEdits();
  });

  onUnmounted(() => store.clearCurrentTheme());
</script>

<template>
  <ThreePanePage
    :span-center="7"
    data-cy="report-theme-detail">
    <template #center>
      <LoadingState
        v-if="loading && !currentTheme"
        class="py-16" />

      <Card
        v-else-if="currentTheme"
        class="p-6">
        <CardHeader class="pb-2">
          <CardTitle class="heading-page">{{ currentTheme.name }}</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion
            v-model="openSection"
            type="single"
            collapsible>
            <ReportThemeGeneralSection />
            <ReportThemeSkinSection
              v-for="skinSection in skinSections"
              :key="skinSection.key"
              :skin-section="skinSection"
              :detected-fonts="detectedFonts" />
            <ReportThemeChartsSection :detected-fonts="detectedFonts" />
          </Accordion>
        </CardContent>
      </Card>
    </template>
  </ThreePanePage>
</template>
